import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { NavLink } from 'react-router-dom';
import Button from '../Button';
import { useAdminMenuItems } from './adminMenuConfig';
import '@fortawesome/fontawesome-free/css/all.min.css';

interface AdminMobileNavProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Off-canvas admin section navigation for viewports below md.
 * Reuses .sb-drawer-panel (opaque MB) + .sb-drawer-overlay (RS) from spec 016.
 */
const AdminMobileNav: React.FC<AdminMobileNavProps> = ({ isOpen, onClose }) => {
  const menuItems = useAdminMenuItems();

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleContentClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('a[href]')) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className="sb-drawer-overlay" aria-hidden="true" onClick={onClose} />
      <aside
        className="sb-drawer-panel sb-drawer-panel--left sb-drawer-panel--open admin-mobile-nav"
        role="dialog"
        aria-modal="true"
        aria-label="Admin menüsü"
      >
        <header className="sb-drawer-panel-header d-flex align-items-center justify-content-between px-3 py-3 border-bottom border-secondary-subtle">
          <span className="fw-bold text-primary">
            <i className="fas fa-shield-alt me-2" />
            Admin Menü
          </span>
          <Button
            type="button"
            className="btn btn-link p-2"
            style={{ color: 'var(--sb-text)' }}
            onClick={onClose}
            aria-label="Menüyü kapat"
          >
            <i className="fas fa-times fa-lg" />
          </Button>
        </header>
        <nav
          className="sb-drawer-panel-body overflow-auto flex-grow-1 min-h-0 py-2"
          onClick={handleContentClick}
        >
          <ul className="nav flex-column px-2">
            {menuItems.map(({ path, label, icon }) => (
              <li key={path} className="nav-item">
                <NavLink
                  className={({ isActive }) =>
                    `nav-link rounded ${isActive ? 'active bg-primary text-white' : ''}`
                  }
                  to={path}
                  onClick={onClose}
                >
                  <i className={`fas ${icon} me-2`} />
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </aside>
    </>,
    document.body
  );
};

export default AdminMobileNav;
